import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {UniversityService} from '../../../shared/services/university.service';
import {AlertService} from '../../../shared/services/alert.service';
import {GlobalService} from '../../../shared/services/global.service';
import {UtilsService} from '../../../shared/services/utils.service';

// Accepts only a bare YouTube video ID or a plain YouTube URL — no iframe/script
// embed code, so the whole field value must match one of these two shapes.
const YOUTUBE_URL_PATTERN = /^(?:[\w-]{10,12}|https?:\/\/(www\.|m\.)?(youtube\.com\/(watch\?v=|embed\/|shorts\/)[\w-]{10,12}(&\S*)?|youtu\.be\/[\w-]{10,12}(\?\S*)?))$/i;
const YOUTUBE_ID_PATTERN = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{10,12})/i;

function getYoutubeVideoId(input: string): string | null {
    if (!input) {
        return null;
    }
    const trimmed = input.trim();
    const match = trimmed.match(YOUTUBE_ID_PATTERN);
    return match ? match[1] : (/^[\w-]{10,12}$/.test(trimmed) ? trimmed : null);
}

function getYoutubeEmbedUrl(input: string): string | null {
    const videoId = getYoutubeVideoId(input);
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
}

// Falls back to YouTube's own default thumbnail when no custom thumbnail was uploaded.
function getYoutubeThumbnailUrl(input: string): string | null {
    const videoId = getYoutubeVideoId(input);
    return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;
}

@Component({
    selector: 'app-university-detail-shell',
    templateUrl: './university-detail-shell.component.html',
    styleUrls: ['./university-detail-shell.component.scss']
})
export class UniversityDetailShellComponent implements OnInit {
    public toggleMenu = false;
    public universityId: any;
    university: any;
    updateVideoLinkForm: FormGroup;
    submitted = false;
    submitting = false;
    onFileChanged: any;
    uploadingCampusThumbnail = false;
    campusThumbnailPreviewUrl: any;
    campusVideoEmbedUrl: SafeResourceUrl;

    constructor(private router: Router,
                private activatedRoute: ActivatedRoute,
                private fb: FormBuilder,
                public modalService: NgbModal,
                private sanitizer: DomSanitizer,
                private alertService: AlertService,
                private globalService: GlobalService,
                private universityService: UniversityService) {
    }

    ngOnInit(): void {
        this.universityId = this.activatedRoute.snapshot.params['id'];
        this.updateVideoLinkForm = this.fb.group({
            'campus_video_url': ['', Validators.pattern(YOUTUBE_URL_PATTERN)],
            'campus_video_thumbnail_file_id': [''],
            'campus_video_source': ['']
        });
        this.getUniversityDetail();
    }

    getUniversityDetail() {
        this.universityService.detail(this.universityId).subscribe(data => {
            if (data) {
                this.university = data;
            }
        });
    }

    goTo(tab) {
        this.router.navigateByUrl('university/' + this.universityId + '/' + tab);
    }

    goBack(): void {
        this.router.navigateByUrl('universities');
    }

    get campusVideoThumbnailUrl(): string | undefined {
        const customThumbnail = this.university && this.university['campus_video_thumbnail'] && this.university['campus_video_thumbnail']['url'];
        if (customThumbnail) {
            return customThumbnail;
        }
        return getYoutubeThumbnailUrl(this.university && this.university['campus_video_url']) || undefined;
    }

    openCardStatsModal(content) {
        this.modalService.open(content, {size: 'lg'});
    }

    openCampusVideoPreview(content) {
        const embedUrl = getYoutubeEmbedUrl(this.university && this.university['campus_video_url']);
        if (!embedUrl) {
            return;
        }
        this.campusVideoEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
        this.modalService.open(content, {size: 'lg'});
    }

    updateVideoLinkModal(content) {
        this.submitted = false;
        this.updateVideoLinkForm.patchValue({
            'campus_video_url': (this.university && this.university['campus_video_url']) ? this.university['campus_video_url'] : '',
            'campus_video_thumbnail_file_id': (this.university && this.university['campus_video_thumbnail_file_id']) ? this.university['campus_video_thumbnail_file_id'] : '',
            'campus_video_source': (this.university && this.university['campus_video_source']) ? this.university['campus_video_source'] : ''
        });
        this.campusThumbnailPreviewUrl = (this.university && this.university['campus_video_thumbnail']) ? this.university['campus_video_thumbnail']['url'] : undefined;
        this.modalService.open(content, {'size': 'sm'});
    }

    onCampusThumbnailSelected(event) {
        this.uploadingCampusThumbnail = true;
        const params = {
            type: 'UNIVERSITY_CAMPUS_VIDEO_THUMBNAIL',
            fileType: 'NORMAL',
            university_id: this.universityId,
            user_id: this.globalService.getSelf() ? this.globalService.getSelf().userId : ''
        };
        this.universityService.fileUpload(params, event).subscribe(data => {
            this.uploadingCampusThumbnail = false;
            if (data && data['data']) {
                this.updateVideoLinkForm.patchValue({'campus_video_thumbnail_file_id': data['data'].id});
                this.campusThumbnailPreviewUrl = data['data'].url;
            }
        }, error => {
            this.uploadingCampusThumbnail = false;
        });
    }

    updateVideoLink() {
        this.submitted = true;
        if (!this.updateVideoLinkForm.valid) {
            return;
        }
        const campusVideoUrl = (this.updateVideoLinkForm.value && this.updateVideoLinkForm.value.campus_video_url) ? this.updateVideoLinkForm.value.campus_video_url : '';
        const params = {
            'campus_video_url': campusVideoUrl ? UtilsService.encodeHtml(campusVideoUrl) : '',
            'campus_video_thumbnail_file_id': (this.updateVideoLinkForm.value && this.updateVideoLinkForm.value.campus_video_thumbnail_file_id) ? this.updateVideoLinkForm.value.campus_video_thumbnail_file_id : '',
            'campus_video_source': (this.updateVideoLinkForm.value && this.updateVideoLinkForm.value.campus_video_source) ? this.updateVideoLinkForm.value.campus_video_source : ''
        };
        this.submitting = true;
        this.universityService.updateCampusVideoUrl(this.universityId, params).subscribe(data => {
            this.submitting = false;
            this.submitted = false;
            this.modalService.dismissAll('');
            this.alertService.showSuccess({title: 'Success', message: 'Video link updated successfully'});
            this.getUniversityDetail();
        }, error => {
            this.submitting = false;
            this.submitted = false;
        });
    }

    onBrochureFileChange(e) {
        this.onFileChanged = e;
        this.universityService.fileUploadAdmin({
            'type': 'UNIVERSITY_BROCHURE',
            'fileType': 'NORMAL',
            'university_id': this.universityId
        }, this.onFileChanged).subscribe(data => {
            this.alertService.showSuccess({title: 'Success', message: 'Uploaded Successfully'});
            this.getUniversityDetail();
        }, error => {
            this.alertService.showErrors('Something went wrong, please try again');
        });
    }

    openUniversityBrochure() {
        window.open(this.university['brochure_file']['url'], '_blank');
    }
}
